/**
 * Modal for create / accept / reject / view department transfers.
 * Layout matches LocationTransferModal (transfer-modal-* styles).
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import TransferActor from '../../transfers/components/TransferActor';
import TransferViewDetails from '../../transfers/components/TransferViewDetails';
import { formatTransferDateTime } from '../../transfers/transferDateUtils';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import '../../transfers/components/DeviceTransferModal.css';
import '../../transfers/components/TransferRouteStack.css';

const DepartmentTransferModal = ({
  token,
  user,
  mode = 'create',
  device = null,
  transfer = null,
  onClose,
  onSuccess,
}) => {
  const [toDepartmentId, setToDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [custodianId, setCustodianId] = useState('');
  const [custodians, setCustodians] = useState([]);
  const [requestReason, setRequestReason] = useState('');
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [primaryTechId, setPrimaryTechId] = useState('');
  const [secondaryTechId, setSecondaryTechId] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [decisionReason, setDecisionReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState(null);

  const deviceId = device?.id || transfer?.device_id;
  const isCreate = mode === 'create';
  const isAccept = mode === 'accept';
  const isReject = mode === 'reject';
  const isCancel = mode === 'cancel';
  const isView = mode === 'view';

  useEffect(() => {
    if (!token || !deviceId || !isCreate) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          API_ENDPOINTS.DEPARTMENT_TRANSFERS.DESTINATION_DEPARTMENTS(deviceId),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setDepartments(res.data?.departments || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load departments');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, deviceId, isCreate]);

  useEffect(() => {
    if (!token || !toDepartmentId || !isCreate) {
      setCustodians([]);
      setCustodianId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          API_ENDPOINTS.USERS.BY_ROLE('custodian', { department_id: toDepartmentId }),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          const list = (res.data?.users || res.data || []).filter(
            (u) => u.employee_id !== user?.employee_id
          );
          setCustodians(list);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load custodians');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, toDepartmentId, isCreate, user?.employee_id]);

  useEffect(() => {
    if (!token || !isAccept || !transfer?.to_department_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          API_ENDPOINTS.USERS.TECHNICIANS({ department_id: transfer.to_department_id }),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setTechnicians(res.data?.users || res.data?.technicians || res.data || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load technicians');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, isAccept, transfer?.to_department_id]);

  const submitCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        API_ENDPOINTS.DEPARTMENT_TRANSFERS.CREATE,
        {
          device_id: deviceId,
          to_department_id: parseInt(toDepartmentId, 10),
          destination_custodian_employee_id: custodianId,
          request_reason: requestReason.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create department transfer');
    } finally {
      setLoading(false);
    }
  };

  const submitAccept = async () => {
    setLoading(true);
    setPendingAction('accept');
    setError(null);
    try {
      await axios.post(
        API_ENDPOINTS.DEPARTMENT_TRANSFERS.ACCEPT_DESTINATION(transfer.transfer_id || transfer.id),
        {
          destination_physical_location: physicalLocation.trim(),
          destination_primary_technician_id: parseInt(primaryTechId, 10),
          destination_secondary_technician_id: secondaryTechId
            ? parseInt(secondaryTechId, 10)
            : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept department transfer');
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const submitReject = async () => {
    const reason = (isAccept ? rejectionReason : decisionReason).trim();
    if (!reason) return;
    setLoading(true);
    setPendingAction('reject');
    setError(null);
    try {
      await axios.post(
        API_ENDPOINTS.DEPARTMENT_TRANSFERS.REJECT_DESTINATION(transfer.transfer_id || transfer.id),
        { destination_decision_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject department transfer');
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const submitCancel = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        API_ENDPOINTS.DEPARTMENT_TRANSFERS.CANCEL(transfer.transfer_id || transfer.id),
        { cancellation_reason: decisionReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel department transfer');
    } finally {
      setLoading(false);
    }
  };

  const title = isCreate
    ? 'Transfer Department'
    : isAccept
      ? '📋 Accept department transfer (destination)'
      : isReject
        ? 'Reject department transfer'
        : isCancel
          ? 'Cancel department transfer'
          : 'Department transfer';

  const deviceName = transfer?.device_name || device?.name || device?.device_name || 'Device';
  const deviceIdentifier = transfer?.device_identifier || device?.device_identifier;

  const renderDecisionHistory = (t) => {
    if (!t) return null;
    const blocks = [];
    const { status } = t;

    if (t.reviewed_at_destination || t.destination_decision_reason) {
      const rejected = status === 'rejected_by_destination';
      blocks.push(
        <div
          key="destination"
          className={`transfer-decision-block ${rejected ? 'transfer-decision-block--reject' : 'transfer-decision-block--approve'}`}
        >
          <h4>{rejected ? 'Rejected at destination' : 'Accepted at destination'}</h4>
          {(t.reviewed_by_destination_name || t.reviewed_by_destination_role) && (
            <p className="transfer-decision-detail transfer-decision-actor">
              <span className="transfer-decision-label">By</span>
              <span className="transfer-decision-value">
                <TransferActor
                  name={t.reviewed_by_destination_name}
                  role={t.reviewed_by_destination_role}
                />
              </span>
            </p>
          )}
          {t.reviewed_at_destination && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">When</span>
              <span className="transfer-decision-value">
                {formatTransferDateTime(t.reviewed_at_destination)}
              </span>
            </p>
          )}
          {t.destination_decision_reason && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">{rejected ? 'Reason' : 'Comment'}</span>
              <span className="transfer-decision-value">{t.destination_decision_reason}</span>
            </p>
          )}
        </div>
      );
    }

    if (status === 'cancelled' && (t.cancellation_reason || t.updated_at)) {
      blocks.push(
        <div key="cancelled" className="transfer-decision-block transfer-decision-block--reject">
          <h4>Cancelled by initiator</h4>
          {(t.requested_by_name || t.requested_by_role) && (
            <p className="transfer-decision-detail transfer-decision-actor">
              <span className="transfer-decision-label">By</span>
              <span className="transfer-decision-value">
                <TransferActor name={t.requested_by_name} role={t.requested_by_role} />
              </span>
            </p>
          )}
          {t.updated_at && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">When</span>
              <span className="transfer-decision-value">
                {formatTransferDateTime(t.updated_at)}
              </span>
            </p>
          )}
          {t.cancellation_reason && (
            <p className="transfer-decision-detail">
              <span className="transfer-decision-label">Reason</span>
              <span className="transfer-decision-value">{t.cancellation_reason}</span>
            </p>
          )}
        </div>
      );
    }

    if (blocks.length === 0) return null;

    return (
      <div className="transfer-decision-history">
        <h4 className="transfer-decision-history-title">Decision history</h4>
        {blocks}
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div
      className="transfer-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`transfer-modal-content${loading ? ' transfer-modal-content--busy' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="department-transfer-modal-title"
        aria-busy={loading}
      >
        <div className="transfer-modal-header">
          <h2 id="department-transfer-modal-title">{title}</h2>
          <ModalCloseButton onClick={onClose} title="Close" disabled={loading} />
        </div>

        <div className="transfer-modal-body">
          {error && <div className="transfer-modal-error">{error}</div>}

          {(transfer || device) && (
            <div className="transfer-modal-device-header">
              <h3>{deviceName}</h3>
              {deviceIdentifier && (
                <DeviceIDBadge deviceId={deviceIdentifier} size="small" />
              )}
            </div>
          )}

          {isCreate && (
            <>
              <p className="transfer-modal-hint">
                Hand off this device to another department at the same location. The recipient
                must accept before the change applies.
              </p>
              <label>
                Destination department *
                <select
                  value={toDepartmentId}
                  onChange={(e) => setToDepartmentId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Recipient *
                <select
                  value={custodianId}
                  onChange={(e) => setCustodianId(e.target.value)}
                  disabled={!toDepartmentId || loading || custodians.length === 0}
                >
                  <option value="">
                    {!toDepartmentId
                      ? 'Select department first'
                      : custodians.length === 0
                        ? 'No custodians available'
                        : 'Select recipient'}
                  </option>
                  {custodians.map((c) => (
                    <option key={c.employee_id} value={c.employee_id}>
                      {c.full_name || c.employee_id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reason for transfer *
                <textarea
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  rows={3}
                  disabled={loading}
                  placeholder="Why is this device being transferred?"
                />
              </label>
            </>
          )}

          {(isView || isAccept || isReject || isCancel) && transfer && (
            <TransferViewDetails transfer={transfer} variant="department" />
          )}

          {(isView || isAccept || isReject || isCancel) && transfer && renderDecisionHistory(transfer)}

          {isAccept && (
            <>
              <label>
                Physical location *
                <input
                  type="text"
                  value={physicalLocation}
                  onChange={(e) => setPhysicalLocation(e.target.value)}
                  disabled={loading}
                  placeholder={transfer?.previous_physical_location || 'Room / bay / shelf'}
                />
              </label>
              <label>
                Primary technician *
                <select
                  value={primaryTechId}
                  onChange={(e) => setPrimaryTechId(e.target.value)}
                  disabled={loading || technicians.length === 0}
                >
                  <option value="">Select primary technician</option>
                  {technicians.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.full_name || t.employee_id}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Secondary technician
                <select
                  value={secondaryTechId}
                  onChange={(e) => setSecondaryTechId(e.target.value)}
                  disabled={loading || technicians.length === 0}
                >
                  <option value="">None</option>
                  {technicians
                    .filter((t) => String(t.id) !== String(primaryTechId))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.full_name || t.employee_id}
                      </option>
                    ))}
                </select>
              </label>
              <div className="transfer-action-section">
                <label>
                  Rejection reason (required to reject)
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={2}
                    disabled={loading}
                    placeholder="Why are you rejecting this transfer?"
                  />
                </label>
              </div>
            </>
          )}

          {(isReject || isCancel) && (
            <label>
              {isCancel ? 'Cancellation reason *' : 'Rejection reason *'}
              <textarea
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                rows={3}
                disabled={loading}
                placeholder={
                  isCancel
                    ? 'Why are you cancelling this transfer?'
                    : 'Why are you rejecting this transfer?'
                }
              />
            </label>
          )}
        </div>

        <div className="transfer-modal-footer">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
          {isCreate && (
            <button
              type="button"
              className="action-btn action-primary"
              disabled={
                loading || !toDepartmentId || !custodianId || !requestReason.trim()
              }
              onClick={submitCreate}
            >
              Submit transfer
            </button>
          )}
          {isAccept && (
            <>
              <button
                type="button"
                className="action-btn action-reject"
                disabled={loading || !rejectionReason.trim()}
                onClick={submitReject}
              >
                {pendingAction === 'reject' ? 'Rejecting…' : '⚠️ Reject'}
              </button>
              <button
                type="button"
                className="action-btn action-approve"
                disabled={loading || !physicalLocation.trim() || !primaryTechId}
                onClick={submitAccept}
              >
                {pendingAction === 'accept' ? 'Accepting…' : '✅ Accept at destination'}
              </button>
            </>
          )}
          {isReject && (
            <button
              type="button"
              className="action-btn action-reject"
              disabled={loading || !decisionReason.trim()}
              onClick={submitReject}
            >
              Reject
            </button>
          )}
          {isCancel && (
            <button
              type="button"
              className="action-btn action-primary"
              disabled={loading || !decisionReason.trim()}
              onClick={submitCancel}
            >
              Cancel transfer
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DepartmentTransferModal;
